import { postLogReport } from "@apis/http/logApi";
import { LOCALSTORAGE_KEY } from "@config";
import { store } from "@store/store";
import { getDeviceUUid } from "@utils/util";
import Cookies from "js-cookie";
import { EMPTY, Subject, from, interval, merge } from "rxjs";
import { buffer, catchError, concatMap, filter, map, retry } from "rxjs/operators";
import UAParser from "ua-parser-js";

type ReportEvent = Record<string, any>;

const immediateEvent$ = new Subject<ReportEvent>();
const bufferedEvent$ = new Subject<ReportEvent>();
const flushSignal$ = new Subject<void>();
let pendingFailedEvents: ReportEvent[] = [];

const immediateBatch$ = immediateEvent$.pipe(map((eventItem) => [eventItem]));
const bufferedBatch$ = bufferedEvent$.pipe(buffer(merge(interval(3000), flushSignal$)));

merge(immediateBatch$, bufferedBatch$)
  .pipe(
    map((events) => {
      if (pendingFailedEvents.length === 0) return events;
      const nextBatch = [...pendingFailedEvents, ...events];
      pendingFailedEvents = [];
      return nextBatch;
    }),
    filter((events) => events.length > 0),
    concatMap((events) =>
      from(postLogReport(events)).pipe(
        retry(1),
        catchError(() => {
          pendingFailedEvents = [...events, ...pendingFailedEvents];
          return EMPTY;
        }),
      ),
    ),
  )
  .subscribe();

interface EventParam {
  kalo_pos?: string;
  kalo_pathname?: string;
  kalo_value?: string | object | any;
  kalo_type?: any;
  duration?: number;
  pre_pathname?: string;
  kalo_pre_pathname?: string;
  kalo_username?: string;
  kalo_json?: object;
}

const event = (eventname: string, params: EventParam = {}, callback?: Function) => {
  const kalo_pathname = params?.kalo_pathname || sessionStorage.getItem("cur_pathname") || window.location.pathname;
  let pre_pathname = params?.pre_pathname || sessionStorage.getItem("pre_pathname");
  setTimeout(async () => {
    const state = store.getState();
    const globalReducer = state.globalReducer;
    let kalo_value = params?.kalo_value || "";
    let kalo_type = params?.kalo_type || "";
    kalo_value = typeof kalo_value === "string" ? kalo_value : JSON.stringify(kalo_value);

    // 仍有部分用户存储错误格式，并且不是登录态，刷新页面无法更新标识的错误手机号
    let userName = localStorage.getItem("userName") || globalReducer.username;
    if (!userName.startsWith("PHONE:")) {
      userName = globalReducer.userInfo.username || "visiter";
      localStorage.removeItem("userName");
    }
    const commonField = await getCommonField();
    const preSpm = sessionStorage.getItem("preSpm");
    const kalo_country = globalReducer.region;
    const kalo_language = globalReducer.language;
    const kalo_currency = globalReducer.currency;
    const newReportField = {
      ...commonField,
      url: window.location.href,
      kalo_event_time: new Date().getTime(),
      kalo_event_name: eventname,
      kalo_language,
      kalo_currency,
      kalo_username: userName,
      kalo_tc: Cookies.get(LOCALSTORAGE_KEY.TRACK_CODE) || globalReducer.trackCode,
      kalo_referrer: document.referrer,
      kalo_value: typeof params?.kalo_value === "string" ? params?.kalo_value : JSON.stringify(params?.kalo_value),
      kalo_type,
      spm: [preSpm, getSpm(params?.kalo_pos || "", kalo_type)],
      kalo_pathname,
      kalo_pos: params?.kalo_pos || "",
      pre_pathname: pre_pathname || sessionStorage.getItem("pre_pathname") || document.referrer,
      kalo_navigator_language: navigator.language,
      kalo_country,
      kalo_json: params?.kalo_json || {},
    };

    if (eventname === "kalo_click") {
      sessionStorage.setItem("preSpm", getSpm(params?.kalo_pos || "", kalo_type));
    }

    if (import.meta.env.MODE === "production" || import.meta.env.MODE === "staging") {
      if (newReportField.kalo_event_name === "kalo_click") {
        immediateEvent$.next(newReportField);
      } else {
        bufferedEvent$.next(newReportField);
      }
    }

    if (import.meta.env.MODE !== "production") {
      console.log(
        newReportField.kalo_event_name,
        params?.kalo_pos || params?.kalo_value || window.location.pathname,
        "日志:",
        newReportField,
      );
    }
    if (callback) {
      callback();
    }
  }, 0);
};

const click = (params: EventParam = {}, callback?: Function) => {
  event("kalo_click", { ...params }, callback);
};

const focus = (params: EventParam = {}, callback?: Function) => {
  event("kalo_focus", { ...params }, callback);
};

const exposure = (params: EventParam = {}, callback?: Function) => {
  event("kalo_exposure", { ...params }, callback);
};

const hover = (params: object = {}, callback?: Function) => {
  event("kalo_hover", { ...params }, callback);
};

const duration = (params: any, callback?: Function) => {
  event("kalo_duration", { ...params, kalo_value: params?.duration }, callback);
};

const reportError = (error: any) => {
  try {
    if (typeof error === "string") {
      event("kalo_error", { kalo_value: error });
    } else if (error.message) {
      event("kalo_error", { kalo_value: error.message });
    }
  } catch (err) {
    console.error(err, error);
  }
};

export const reportImmediately = async () => {
  flushSignal$.next();
};

export default {
  event,
  click,
  focus,
  exposure,
  hover,
  duration,
  error: reportError,
  reportImmediately,
};

let COMMON_FIELD: any;

const getCommonField = async () => {
  if (COMMON_FIELD) {
    return COMMON_FIELD;
  } else {
    const uaParser = new UAParser();
    const ua = uaParser.getResult();
    COMMON_FIELD = {
      session_id: getSessionID(),
      kalo_uuid: await getDeviceUUid(),
      // @ts-ignore
      network_type: navigator?.connection?.effectiveType,
      device_type: "pc",
      os_type: ua.os.name,
      medium_type: "browser",
      medium_name: ua.browser.name,
      kalo_version: CLIP_VERSION,
      env: import.meta.env.MODE,
      ua: ua.ua,
      kalo_referrer: document.referrer,
      project: "kaloclip",
    };
    return COMMON_FIELD;
  }
};

export const getSessionID = () => {
  return Cookies.get("page_session") || "";
};

const getSpm = (kalo_pos: string, kalo_type: string) => {
  const spma = window.location.host.replace(/\./g, "").replace("com", "");
  const spmb = window.location.pathname;
  const spmList = [spma, spmb];
  if (kalo_pos) {
    spmList.push(kalo_pos);
  }
  if (kalo_type) {
    spmList.push(kalo_type);
  }
  return spmList.join(".");
};

export const reportKaloPageload = () => {
  let kalo_type = "";
  try {
    const performaces = window.performance.getEntries();
    const kalo_json = [];
    for (let i = 0; i < Math.min(performaces.length, 30); i++) {
      let performace = performaces[i];
      kalo_json.push(performace);
      // @ts-ignore
      if (performace.name.includes("/assets/index") && performace?.deliveryType === "cache") {
        kalo_type = "cache";
      }
      if (performace.name === "first-contentful-paint") {
        break;
      }
    }
    event("kalo_pageload", { kalo_value: performance.now(), kalo_json, kalo_type });
  } catch {}
};
